'use client';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { isDeliveryMethod } from '@/entities/venue/lib/isDeliveryMethod';
import { useDeliveryMethodAvailability } from '@/entities/venue/lib/useDeliveryMethodAvailability';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { QueryApi } from '@/features/query/api';
import { CartContext } from './cart-context';

/**
 * Keeps the selected delivery method consistent with the available list. Lives
 * inside the cart context (unlike the provider itself) so it can use the full
 * availability check — including the cart-subtotal-dependent minimum-order rule.
 *
 * - syncs `deliveryMethod.type` while the selected method still exists;
 * - when it disappears, falls back to the first *available* method of the same
 *   type, keeping the type even if none of them are currently available.
 */
export const CartDeliveryMethodSync = () => {
    const { formMethods } = useContext(CartContext);
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);
    const { findFirstSelectable } = useDeliveryMethodAvailability();
    const selectedDeliveryMethodId = useWatch({
        control: formMethods.control,
        name: 'deliveryMethod.id',
    });

    useEffect(() => {
        const methods = deliveryMethodsQuery.data;
        if (!methods?.length) return;

        const currentMethod = methods.find((m) => m.id === selectedDeliveryMethodId);
        if (currentMethod) {
            const type = isDeliveryMethod(currentMethod) ? DeliveryType.DELIVERY : DeliveryType.PICKUP;
            if (formMethods.getValues('deliveryMethod.type') !== type) {
                formMethods.setValue('deliveryMethod.type', type);
            }
            return;
        }

        const lastType = formMethods.getValues('deliveryMethod.type');
        const sameType = methods.filter(
            (m) => (isDeliveryMethod(m) ? DeliveryType.DELIVERY : DeliveryType.PICKUP) === lastType,
        );
        const pool = sameType.length ? sameType : methods;
        const fallbackMethod = findFirstSelectable(pool);
        if (fallbackMethod) {
            formMethods.setValue('deliveryMethod.id', fallbackMethod.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryMethodsQuery.data, selectedDeliveryMethodId]);

    return null;
};
